

import { useState } from 'react';

// MUI
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import { alpha, useTheme } from '@mui/material/styles';


import { SignalItem } from '../../interface/SignalItem';
import { useEffect } from 'react';
import { LogApi } from '../../api/LogApi';
import { MarketChart } from '../../components/charts/MarketChart';
import { IRunEvent } from '../../interface/IRunEvent';

import moment from 'moment'
import { RunEventTable } from './RunEventTable';
import { Transition } from '../../animations/Transition';
import { dashboardMutedTextSx } from '../dashboardv2/dashboardStyles';

interface SignalDetailsProps
{
  open: boolean
  handleClose: () => void
  signal: SignalItem
}

export const SignalDetails = ({ open, handleClose, signal }: SignalDetailsProps) =>
{
  const theme = useTheme()

  const [ runEvents, setRunEvents ] = useState<IRunEvent[] | undefined>()

  const [ loading, setLoading ] = useState(false)
  const [ chartInterval, setChartInterval ] = useState("15m")


  useEffect(() => {

    getData()
    
  }, [ chartInterval ])

  const getData = async () =>
  {
    setLoading(true)
    await getRunEvents()
    setLoading(false)

  }

  const getRunEvents = async () =>
  {
    try
    {
      const runEvents = await new LogApi().getRunEvents("desc", 100, 0, moment().subtract("2", "weeks").toISOString())

      const runEventsList = []
      if(runEvents)
      {
        for(const runEvent of runEvents)
        {
          if(runEvent.marketId === signal.pair) runEventsList.push(runEvent)
        }
        setRunEvents(runEventsList)
      }

    }
    catch(error)
    {
      // Bit of an issue getting those candles
    }
  }


  return(
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "18px",
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(180deg, ${alpha("#101c2d", 0.98)} 0%, ${alpha("#0a1320", 0.99)} 100%)`
              : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#eef5ff", 0.98)} 100%)`,
          border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 24px 60px ${alpha("#01050d", 0.5)}`
              : `0 24px 60px ${alpha("#9db3d1", 0.22)}`,
        }
      }}
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description">
    <DialogTitle sx={{ px: 3, pt: 3, pb: 2, borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}` }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Box sx={styles.headerIcon(theme)}>
            <RadarRoundedIcon />
          </Box>
        </Grid>
        <Grid item xs>
          <Typography variant="overline" sx={{ letterSpacing: 1.5, color: alpha(theme.palette.text.primary, 0.64) }}>
            Signal Review
          </Typography>
          <Typography variant="h5">Signal Details {signal.pair}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: alpha(theme.palette.text.primary, 0.68) }}>
            Inspect price action, timing, and related bot events for this signal.
          </Typography>
        </Grid>
        <Grid item>
          <IconButton onClick={handleClose} sx={styles.closeButton(theme)}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
    </DialogTitle>
    <DialogContent sx={{ px: 3, py: 2.5 }}>
      <Stack spacing={2.5}>
        <Box sx={styles.hero(theme)}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" sx={{ letterSpacing: 1.4, ...dashboardMutedTextSx(theme) }}>
                Signal Summary
              </Typography>
              <Typography sx={{ fontSize: 30, lineHeight: 1.05, mt: 0.5 }}>
                {signal.coin} {signal.direction} setup from {signal.source}
              </Typography>
              <Typography sx={{ mt: 1.25, ...dashboardMutedTextSx(theme) }}>
                Created {moment(signal.time_generated).fromNow()} with timeout {moment(signal.timeout).fromNow()}.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1} alignItems={{ xs: "flex-start", md: "stretch" }}>
                <Chip label={`Entry ${Number(signal.entry).toFixed(8)}`} sx={styles.summaryChip(theme)} />
                <Chip label={`Take Profit ${Number(signal.profit).toFixed(8)}`} sx={styles.summaryChip(theme)} />
                <Chip label={`Stop Loss ${Number(signal.loss).toFixed(8)}`} sx={styles.summaryChip(theme)} />
              </Stack>
            </Grid>
          </Grid>
        </Box>

      { loading ? <Box sx={styles.chartContainer}><CircularProgress /></Box> : 
      <>


      <Box sx={styles.sectionCard(theme)}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 1.4, ...dashboardMutedTextSx(theme) }}>
              Market Chart
            </Typography>
            <Typography variant="h6">{signal.pair} with live levels</Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {["1m","3m","5m","15m","30m","1h","4h","12h","1d"].map((interval) => (
              <Chip
                key={interval}
                label={interval.toUpperCase()}
                clickable
                onClick={() => setChartInterval(interval)}
                sx={styles.intervalChip(theme, chartInterval === interval)}
              />
            ))}
          </Stack>
        </Stack>

        <MarketChart
          height={400}
          tradingPair={signal.pair}
          chartInterval={chartInterval}
          entryPrice={signal.entry}
          currentPrice={signal.currentPrice}
          takeProfit={signal.profit}
          stopLoss={signal.loss}
        />
      </Box>
      </>
      }
      </Stack>
    </DialogContent>
  
  </Dialog>
  )
}

const styles = {
  chartContainer: {
    height: 400,
    flex: 1,
    display: 'flex' as 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  headerIcon: (theme: any) => ({
    width: 44,
    height: 44,
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    color: theme.palette.info.main,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.16 : 0.14),
  }),
  closeButton: (theme: any) => ({
    width: 40,
    height: 40,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.7),
  }),
  hero: (theme: any) => ({
    p: 2.5,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#102946", 0.7)} 0%, ${alpha("#0c1727", 0.85)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.9)} 0%, ${alpha("#e8f2ff", 0.9)} 100%)`,
  }),
  summaryChip: (theme: any) => ({
    justifyContent: "flex-start",
    borderRadius: "999px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.72),
    color: theme.palette.text.primary,
  }),
  sectionCard: (theme: any) => ({
    p: 2,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.58),
  }),
  emptyState: (theme: any) => ({
    p: 2.5,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.08 : 0.08),
    display: "flex",
    flexDirection: "column",
    gap: 0.75,
    alignItems: "center",
    justifyContent: "center",
  }),
  intervalChip: (theme: any, isActive: boolean) => ({
    borderRadius: "999px",
    minWidth: 58,
    color: isActive ? theme.palette.common.white : theme.palette.text.primary,
    background: isActive
      ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.info.main})`
      : alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.06 : 0.7),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.4)}`,
  }),
}
